import {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {PhoneHQIconAvatar, PhoneHQGlyph, PhoneHQAvatar, PhoneHQInput} from 'ch3-ui-lib';
import {useSelector} from "react-redux";
import {debounce} from "lodash";
import {Building, Folder, Lock, Trash2, type LucideIcon} from "lucide-react";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {User, useSearchUsersQuery} from "../../store/features/Users/userService";
import {FetchGroupsResponse, GroupsList, useFetchGroupsQuery} from "../../store/features/groups/groupsService";
import {Colors} from "../../utilis/types/Types";
import {getFullName} from "../../utilis/getFullName";

const GROUPS_OWNER = "access-selector";

export interface GroupsCursor {
    lastName?: string;
    lastGroupId?: number;
}

export const nextGroupsCursor = (groupData: GroupsList | undefined): GroupsCursor | null => {
    const groups = groupData?.groups ?? [];
    const lastGroup = groups.at(-1);
    if (!lastGroup || groups.length >= (groupData?.totalCount ?? 0)) {
        return null;
    }
    return {lastName: lastGroup.group.name, lastGroupId: lastGroup.group.groupId};
};

export type AccessMode = "PRIVATE" | "PUBLIC" | "SHARED";

const ACCESS_OPTIONS: {value: AccessMode; icon: LucideIcon; label: string; description: string}[] = [
    {value: "PRIVATE", icon: Lock, label: "Only me", description: "Only you have access"},
    {value: "PUBLIC", icon: Building, label: "Public", description: "Share with the entire workspace"},
    {value: "SHARED", icon: Folder, label: "Shared", description: "Choose which groups or members have access"},
];

export interface SelectedUser {
    userId: number;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
}

export interface SelectedGroup {
    groupId: number;
    name: string;
    icon?: string;
    iconColor?: string;
    memberCount?: number;
}

interface AccessSelectorProps {
    value: AccessMode;
    onChange: (value: AccessMode) => void;
    selectedUsers: SelectedUser[];
    selectedGroups: SelectedGroup[];
    onSelectedUsersChange: (users: SelectedUser[]) => void;
    onSelectedGroupsChange: (groups: SelectedGroup[]) => void;
    /** Skip the user/group search queries while the host (panel/dialog) is closed. */
    active?: boolean;
    disabledOptions?: Readonly<Partial<Record<AccessMode, string>>>;
}

export function AccessSelector({
    value,
    onChange,
    selectedUsers,
    selectedGroups,
    onSelectedUsersChange,
    onSelectedGroupsChange,
    active = true,
    disabledOptions,
}: AccessSelectorProps) {
    const userId = useSelector(selectUserId);

    const [searchPhrase, setSearchPhrase] = useState("");
    const [phrase, setPhrase] = useState("");
    const [dropdownTab, setDropdownTab] = useState<"USERS" | "GROUPS">("USERS");
    const [groupsCursor, setGroupsCursor] = useState<GroupsCursor>({});

    const {data: usersData} = useSearchUsersQuery(phrase, {skip: !active});
    const {data: groupData} = useFetchGroupsQuery({phrase, userId, owner: GROUPS_OWNER, ...groupsCursor}, {skip: !active});

    const handleGroupsScrollEnd = () => {
        const cursor = nextGroupsCursor(groupData);
        if (cursor) setGroupsCursor(cursor);
    };

    const groups = useMemo(() =>
            (groupData?.groups ?? []).map((g: FetchGroupsResponse) => ({
                groupId: g.group.groupId,
                name: g.group.name,
                icon: g.group.icon,
                iconColor: g.group.iconColor,
                memberCount: g.users?.length,
            })),
        [groupData]
    );

    const debouncedSetPhrase = useMemo(
        () => debounce((val: string) => {
            setGroupsCursor({});
            setPhrase(val);
        }, 500),
        []
    );

    useEffect(() => () => debouncedSetPhrase.cancel(), [debouncedSetPhrase]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchPhrase(val);
        debouncedSetPhrase(val);
    };

    const filteredUsers = (usersData ?? []).filter(
        (u: User) => !selectedUsers.find(su => su.userId === u.userId)
    );

    const filteredGroups = groups.filter(
        (group) => !selectedGroups.find(selectedGroup => selectedGroup.groupId === group.groupId)
    );

    const handleSelectUser = (user: User) => {
        onSelectedUsersChange([...selectedUsers, {
            userId: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
            avatarUrl: user.avatarUrl,
        }]);
        setSearchPhrase("");
        setGroupsCursor({});
        setPhrase("");
    };

    const handleSelectGroup = (group: SelectedGroup) => {
        onSelectedGroupsChange([...selectedGroups, {
            groupId: group.groupId,
            name: group.name,
            icon: group.icon,
            iconColor: group.iconColor,
            memberCount: group.memberCount,
        }]);
        setSearchPhrase("");
        setGroupsCursor({});
        setPhrase("");
    };

    const handleRemoveUser = (uid: number) => {
        onSelectedUsersChange(selectedUsers.filter(u => u.userId !== uid));
    };

    const handleRemoveGroup = (gid: number) => {
        onSelectedGroupsChange(selectedGroups.filter(g => g.groupId !== gid));
    };

    return (
        <div className="bg-surface-muted rounded-card p-1">
            <div
                role="radiogroup"
                aria-label="Contact access"
                className="bg-surface border border-line rounded-[12px] shadow-sm overflow-hidden"
            >
                {ACCESS_OPTIONS.map((option, idx) => {
                    const disabledReason = disabledOptions?.[option.value];
                    const disabled = disabledReason !== undefined;
                    const selectOption = () => {
                        if (!disabled) onChange(option.value);
                    };

                    return (
                        <div
                            key={option.value}
                            role="radio"
                            aria-checked={value === option.value}
                            aria-disabled={disabled}
                            tabIndex={disabled ? -1 : 0}
                            className={`flex gap-4 items-start pl-4 pr-6 py-4 ${
                                disabled ? "cursor-not-allowed bg-amber-50/60" : "cursor-pointer hover:bg-canvas"
                            } ${idx < ACCESS_OPTIONS.length - 1 ? "border-b border-line" : ""}`}
                            onClick={selectOption}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    selectOption();
                                }
                            }}
                        >
                            <div className="flex items-center justify-center w-8 h-10 shrink-0">
                                <option.icon size={18} className={disabled ? "text-amber-700" : "text-ink-muted"}/>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-4">
                                    <div className="flex-1 flex flex-col gap-1 justify-center min-h-10">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-ink">{option.label}</span>
                                            {disabled && (
                                                <span className="rounded-md border border-amber-200 bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                                                    Owner only
                                                </span>
                                            )}
                                        </div>
                                        <span className={disabled ? "text-footnote text-amber-800" : "text-footnote text-ink-muted"}>
                                            {disabledReason ?? option.description}
                                        </span>
                                    </div>
                                    <div className={`size-5 rounded-full border-[1.5px] shrink-0 flex items-center justify-center ${
                                        value === option.value
                                            ? "border-indigo-600 bg-brand"
                                            : disabled ? "border-amber-400 bg-amber-50" : "border-zinc-400"
                                    }`}>
                                        {value === option.value && (
                                            <div className="size-3 rounded-full bg-surface"/>
                                        )}
                                    </div>
                                </div>

                                {option.value === "SHARED" && value === "SHARED" && (
                                    <div className="mt-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
                                    <SharedSearchField
                                        value={searchPhrase}
                                        onChange={handleSearchChange}
                                        hasItems={filteredUsers.length > 0 || filteredGroups.length > 0}
                                        activeTab={dropdownTab}
                                        onTabChange={setDropdownTab}
                                        users={filteredUsers}
                                        groups={filteredGroups}
                                        onSelectUser={handleSelectUser}
                                        onSelectGroup={handleSelectGroup}
                                        onGroupsScrollEnd={handleGroupsScrollEnd}
                                    />

                                    {(selectedGroups.length > 0 || selectedUsers.length > 0) && (
                                        <div className="flex flex-col">
                                            {selectedGroups.map(group => (
                                                <div key={`sg-${group.groupId}`} className="flex items-center gap-3 py-4 border-b border-line">
                                                    <PhoneHQIconAvatar icon={group.icon} color={group.iconColor || Colors.grey} name={group.name} size="24px"/>
                                                    <div className="flex flex-col flex-1 min-w-0">
                                                        <span className="text-footnote font-medium text-ink truncate">{group.name}</span>
                                                        {group.memberCount !== undefined && (
                                                            <span className="text-footnote text-ink-muted">{group.memberCount} members</span>
                                                        )}
                                                    </div>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleRemoveGroup(group.groupId); }}
                                                        className="flex items-center justify-center size-7 rounded-control hover:bg-surface-muted cursor-pointer"
                                                    >
                                                        {/* !p-0 cancels the global `button .lucide` 6px padding so the icon renders at the design's 18px; !text override beats the rule's zinc-800 */}
                                                        <Trash2 size={18} className="!p-0 !text-ink-muted"/>
                                                    </button>
                                                </div>
                                            ))}
                                            {selectedUsers.map(user => (
                                                <div key={`su-${user.userId}`} className="flex items-center gap-3 py-4 border-b border-line last:border-b-0">
                                                    <PhoneHQAvatar
                                                        size="24px"
                                                        type={user.avatarUrl ? "image" : "initials"}
                                                        src={user.avatarUrl}
                                                        name={getFullName(user.firstName, user.lastName)}
                                                        colorFromName
                                                    />
                                                    <span className="flex-1 text-footnote font-medium text-ink truncate">{getFullName(user.firstName, user.lastName)}</span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); handleRemoveUser(user.userId); }}
                                                        className="flex items-center justify-center size-7 rounded-control hover:bg-surface-muted cursor-pointer"
                                                    >
                                                        {/* !p-0 cancels the global `button .lucide` 6px padding so the icon renders at the design's 18px; !text override beats the rule's zinc-800 */}
                                                        <Trash2 size={18} className="!p-0 !text-ink-muted"/>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export interface SharedSearchFieldProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    hasItems: boolean;
    activeTab: "USERS" | "GROUPS";
    onTabChange: (tab: "USERS" | "GROUPS") => void;
    users: User[];
    groups: SelectedGroup[];
    onSelectUser: (user: User) => void;
    onSelectGroup: (group: SelectedGroup) => void;
    onGroupsScrollEnd?: () => void;
}

export function SharedSearchField({
    value,
    onChange,
    hasItems,
    activeTab,
    onTabChange,
    users,
    groups,
    onSelectUser,
    onSelectGroup,
    onGroupsScrollEnd,
}: SharedSearchFieldProps) {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [rect, setRect] = useState<{
        left: number;
        width: number;
        top?: number;
        bottom?: number;
        maxHeight: number;
    } | null>(null);
    const [focused, setFocused] = useState(false);

    // Open the box as soon as the input is clicked/focused, not only while typing.
    const showDropdown = (focused || value.length > 0) && hasItems;

    useLayoutEffect(() => {
        if (!showDropdown || !wrapperRef.current) return;
        const update = () => {
            if (!wrapperRef.current) return;
            const r = wrapperRef.current.getBoundingClientRect();
            const gap = 4;
            const margin = 8;
            const spaceBelow = window.innerHeight - r.bottom - gap - margin;
            const spaceAbove = r.top - gap - margin;
            // Flip above the field when there isn't enough room below and there's
            // more space above, so the dropdown stays fully within the viewport.
            const openUp = spaceBelow < 200 && spaceAbove > spaceBelow;
            if (openUp) {
                setRect({
                    left: r.left,
                    width: r.width,
                    bottom: window.innerHeight - r.top + gap,
                    maxHeight: spaceAbove,
                });
            } else {
                setRect({
                    left: r.left,
                    width: r.width,
                    top: r.bottom + gap,
                    maxHeight: spaceBelow,
                });
            }
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [showDropdown]);

    return (
        <div ref={wrapperRef} className="relative">
            <PhoneHQGlyph name="search" size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none z-10"/>
            <PhoneHQInput
                placeholder="Add groups, numbers or members..."
                className="pl-9 w-full h-[36px]"
                value={value}
                onChange={onChange}
                onFocus={() => setFocused(true)}
                // Delay so a click on a dropdown item registers before it closes.
                onBlur={() => setTimeout(() => setFocused(false), 120)}
            />
            {showDropdown && rect && createPortal(
                <SharedDropdown
                    style={{
                        position: "fixed",
                        top: rect.top,
                        bottom: rect.bottom,
                        left: rect.left,
                        width: rect.width,
                        maxHeight: rect.maxHeight,
                    }}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    users={users}
                    groups={groups}
                    onSelectUser={onSelectUser}
                    onSelectGroup={onSelectGroup}
                    onGroupsScrollEnd={onGroupsScrollEnd}
                />,
                document.body,
            )}
        </div>
    );
}

interface SharedDropdownProps {
    activeTab: "USERS" | "GROUPS";
    onTabChange: (tab: "USERS" | "GROUPS") => void;
    users: User[];
    groups: SelectedGroup[];
    onSelectUser: (user: User) => void;
    onSelectGroup: (group: SelectedGroup) => void;
    onGroupsScrollEnd?: () => void;
    style?: React.CSSProperties;
}

function SharedDropdown({activeTab, onTabChange, users, groups, onSelectUser, onSelectGroup, onGroupsScrollEnd, style}: SharedDropdownProps) {
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const list = listRef.current;
        if (activeTab !== "GROUPS" || !onGroupsScrollEnd || !list) return;
        if (list.clientHeight > 0 && list.scrollHeight <= list.clientHeight) onGroupsScrollEnd();
    }, [activeTab, groups.length]);

    return (
        <div
            style={style}
            // Keep focus on the search input when interacting with the dropdown so it
            // doesn't blur-and-close before the tab switch / item click registers.
            onMouseDown={(e) => e.preventDefault()}
            className="z-[60] flex flex-col overflow-hidden bg-zinc-950 rounded-2xl py-1 shadow-[0px_20px_5px_0px_rgba(0,0,0,0),0px_13px_5px_0px_rgba(0,0,0,0.03),0px_7px_4px_0px_rgba(0,0,0,0.09),0px_3px_3px_0px_rgba(0,0,0,0.15),0px_1px_2px_0px_rgba(0,0,0,0.17)]"
        >
            {/* Tabs */}
            <div className="flex shrink-0 items-center gap-2 border-b border-white/[0.08] px-2.5 pt-1.5 pb-2.5">
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onTabChange("USERS"); }}
                    className={`flex items-center justify-center min-h-9 rounded-control px-3 py-2 text-footnote font-medium text-white ${
                        activeTab === "USERS" ? "bg-surface/[0.12]" : "hover:bg-surface/[0.06]"
                    }`}
                >
                    Users
                </button>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onTabChange("GROUPS"); }}
                    className={`flex items-center justify-center min-h-9 rounded-control px-3 py-2 text-footnote font-medium text-white ${
                        activeTab === "GROUPS" ? "bg-surface/[0.12]" : "hover:bg-surface/[0.06]"
                    }`}
                >
                    Groups
                </button>
            </div>

            {/* List */}
            <div
                ref={listRef}
                className="min-h-0 flex-1 max-h-[280px] overflow-y-auto px-1 py-1"
                onScroll={(e) => {
                    if (activeTab !== "GROUPS" || !onGroupsScrollEnd) return;
                    const el = e.currentTarget;
                    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) onGroupsScrollEnd();
                }}
            >
                {activeTab === "USERS" && users.map((user: User) => (
                    <div
                        key={`u-${user.userId}`}
                        onClick={(e) => { e.stopPropagation(); onSelectUser(user); }}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer hover:bg-surface/[0.06]"
                    >
                        <PhoneHQAvatar
                            size="20px"
                            type="initials"
                            name={getFullName(user.firstName, user.lastName)}
                            colorFromName
                        />
                        <span className="text-footnote font-semibold text-white truncate">
                            {getFullName(user.firstName, user.lastName)}
                        </span>
                    </div>
                ))}
                {activeTab === "USERS" && users.length === 0 && (
                    <div className="px-3 py-3 text-footnote text-white/60">No users found</div>
                )}

                {activeTab === "GROUPS" && groups.map((group) => (
                    <div
                        key={`g-${group.groupId}`}
                        onClick={(e) => { e.stopPropagation(); onSelectGroup(group); }}
                        className="flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer hover:bg-surface/[0.06]"
                    >
                        <div className="flex items-center justify-center size-5 rounded-md bg-zinc-700 text-white text-[11px] font-semibold">
                            {(group.name?.[0] ?? "G").toUpperCase()}
                        </div>
                        <div className="flex flex-col min-w-0">
                            <span className="text-footnote font-semibold text-white truncate">{group.name}</span>
                            {group.memberCount !== undefined && (
                                <span className="text-[11px] text-white/60">{group.memberCount} members</span>
                            )}
                        </div>
                    </div>
                ))}
                {activeTab === "GROUPS" && groups.length === 0 && (
                    <div className="px-3 py-3 text-footnote text-white/60">No groups found</div>
                )}
            </div>
        </div>
    );
}
